import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MetricStrip,
  PageIntro,
  Panel,
  LearningShell,
  RecentTrainingRecordsPanel,
  type RecentTrainingRecordItem,
} from '../features/learning-space/components';
import { useLearningViewer } from '../features/learning-space/useLearningViewer';
import {
  buildLearningEvidenceRecordQueryScope,
  useRecentTrainingAttempts,
} from '../features/learning-space/useRecentTrainingAttempts';
import { api, type MistakeStats, type TrainingAttemptRecord } from '../lib/api';
import { useQuery } from '@tanstack/react-query';
import { buildLearningRouteScope } from '@examflow/learning-space';
import {
  buildRetentionReportPrimaryAction,
  buildRetentionToolBackAction,
} from '../features/learning-space/retentionToolsModel';
import type { UiTone, MetricModel } from '@examflow/learning-space';
import { Flame } from 'lucide-react';

interface DailyAccuracy {
  dateKey: string;
  label: string;
  accuracy: number;
  totalQuestions: number;
  correctCount: number;
}

function toDateKey(ms: number): string {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDateKeyLabel(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  if (!y || !m || !d) return dateKey;
  return new Date(y, m - 1, d).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
}

function calculateStreak(records: TrainingAttemptRecord[]): number {
  if (!records.length) return 0;

  const uniqueDays = new Set<string>();
  for (const r of records) {
    uniqueDays.add(toDateKey(r.completedAt));
  }

  const sortedDays = Array.from(uniqueDays).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));

  const today = toDateKey(Date.now());
  const yesterday = toDateKey(Date.now() - 86400000);

  if (sortedDays[0] !== today && sortedDays[0] !== yesterday) return 0;

  let streak = 1;
  for (let i = 1; i < sortedDays.length; i++) {
    const current = Date.parse(sortedDays[i - 1]!);
    const previous = Date.parse(sortedDays[i]!);
    const diffDays = (current - previous) / 86400000;

    if (diffDays === 1) {
      streak++;
    } else {
      break;
    }
  }

  return streak;
}

function computeAccuracyTrend(records: TrainingAttemptRecord[]): DailyAccuracy[] {
  const now = Date.now();
  const sevenDaysAgo = now - 7 * 86400000;

  const dailyMap = new Map<string, { total: number; correct: number }>();

  for (const r of records) {
    if (r.completedAt < sevenDaysAgo) continue;
    const dateKey = toDateKey(r.completedAt);
    const existing = dailyMap.get(dateKey) ?? { total: 0, correct: 0 };
    existing.total += r.answeredCount;
    existing.correct += r.correctCount;
    dailyMap.set(dateKey, existing);
  }

  const result: DailyAccuracy[] = [];
  for (let i = 6; i >= 0; i--) {
    const dateKey = toDateKey(now - i * 86400000);
    const data = dailyMap.get(dateKey) ?? { total: 0, correct: 0 };
    result.push({
      dateKey,
      label: formatDateKeyLabel(dateKey),
      accuracy: data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0,
      totalQuestions: data.total,
      correctCount: data.correct,
    });
  }

  return result;
}

function computeSectionBreakdown(records: TrainingAttemptRecord[]) {
  const sectionMap = new Map<string, { total: number; correct: number; label: string }>();

  for (const record of records) {
    for (const section of record.sectionSummaries ?? []) {
      const existing = sectionMap.get(section.sectionId) ?? {
        total: 0,
        correct: 0,
        label: section.sectionLabel ?? section.sectionId,
      };
      existing.total += section.answeredCount;
      existing.correct += section.correctCount;
      sectionMap.set(section.sectionId, existing);
    }
  }

  return Array.from(sectionMap.entries())
    .map(([sectionId, data]) => ({
      sectionId,
      label: data.label,
      accuracy: data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0,
      totalQuestions: data.total,
      correctCount: data.correct,
    }))
    .sort((a, b) => b.totalQuestions - a.totalQuestions);
}

function computeSummaryMetrics(records: TrainingAttemptRecord[]) {
  const totalSessions = records.length;
  const totalQuestions = records.reduce((sum, r) => sum + r.answeredCount, 0);
  const totalCorrect = records.reduce((sum, r) => sum + r.correctCount, 0);
  const overallAccuracy =
    totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;

  return { totalSessions, totalQuestions, totalCorrect, overallAccuracy };
}

function AccuracyTrendChart({ data }: { data: DailyAccuracy[] }) {
  const maxAccuracy = 100;
  const chartHeight = 120;
  const hasAnyActivity = data.some((day) => day.totalQuestions > 0);

  if (!hasAnyActivity) {
    return <p className="text-sm text-slate-500">近 7 天还没有训练记录。</p>;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-end justify-between gap-2" style={{ height: chartHeight }}>
        {data.map((day) => {
          const barHeight = day.accuracy > 0 ? (day.accuracy / maxAccuracy) * chartHeight : 4;
          return (
            <div key={day.dateKey} className="flex flex-1 flex-col items-center gap-1">
              <div
                className="w-full max-w-8 rounded-sm transition-all"
                style={{
                  height: `${barHeight}px`,
                  backgroundColor:
                    day.accuracy >= 80
                      ? '#0f8b8d'
                      : day.accuracy >= 60
                        ? '#1f4fd8'
                        : day.accuracy > 0
                          ? '#b86b16'
                          : '#e5e7eb',
                }}
                title={
                  day.totalQuestions > 0
                    ? `${day.label} · ${day.accuracy}% · ${day.totalQuestions} 题`
                    : `${day.label} · 无训练`
                }
              />
              {day.accuracy > 0 ? (
                <span className="text-xs font-semibold text-[#111827]">{day.accuracy}%</span>
              ) : (
                <span className="text-xs text-slate-300">—</span>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex justify-between">
        {data.map((day) => (
          <span key={day.dateKey} className="flex-1 text-center text-xs text-slate-400">
            {day.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function SectionBreakdownPanel({
  breakdown,
}: {
  breakdown: ReturnType<typeof computeSectionBreakdown>;
}) {
  if (!breakdown.length) {
    return <p className="text-sm text-slate-500">暂无分项数据。完成专项训练后会按 Skill 汇总。</p>;
  }

  return (
    <div className="space-y-3">
      {breakdown.map((item) => (
        <div key={item.sectionId} className="grid grid-cols-[1fr_80px_60px] items-center gap-4">
          <span className="text-sm font-semibold text-[#111827]">{item.label}</span>
          <div className="flex items-center gap-2">
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${item.accuracy}%`,
                  backgroundColor:
                    item.accuracy >= 80 ? '#0f8b8d' : item.accuracy >= 60 ? '#1f4fd8' : '#b86b16',
                }}
              />
            </div>
            <span className="text-xs font-semibold text-slate-600">{item.accuracy}%</span>
          </div>
          <span className="text-right text-xs text-slate-400">{item.totalQuestions} 题</span>
        </div>
      ))}
    </div>
  );
}

function StreakDisplay({ streak }: { streak: number }) {
  return (
    <div className="flex items-center gap-3">
      <Flame
        className={`h-6 w-6 ${
          streak >= 7 ? 'text-[#0f8b8d]' : streak >= 3 ? 'text-[#1f4fd8]' : 'text-[#b86b16]'
        }`}
      />
      <div>
        <div className="text-2xl font-bold text-[#111827]">{streak}</div>
        <div className="text-xs text-slate-500">天连续训练</div>
        {streak === 0 ? (
          <p className="mt-2 text-xs text-slate-400">今天或昨天完成一组训练即可开始连胜。</p>
        ) : null}
      </div>
    </div>
  );
}

function MistakeStatsPanel() {
  const { data: stats, isLoading, isError } = useQuery({
    queryKey: ['mistake-stats'],
    queryFn: () => api.mistakes.stats() as Promise<MistakeStats>,
  });

  if (isLoading) {
    return (
      <Panel title="错题统计" subtitle="归档错题概况">
        <div className="text-sm text-slate-500">加载中...</div>
      </Panel>
    );
  }

  if (isError || !stats) {
    return (
      <Panel title="错题统计" subtitle="归档错题概况">
        <p className="text-sm text-slate-500">暂时无法加载错题统计</p>
      </Panel>
    );
  }

  return (
    <Panel title="错题统计" subtitle="归档错题概况；闭环复盘请走工作台处方">
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-[8px] border border-[#dbe4d8] bg-[#f8faf6] p-4 text-center">
          <div className="text-2xl font-bold text-[#c94a3a]">{stats.totalMistakes}</div>
          <div className="mt-1 text-xs text-slate-500">总错题数</div>
        </div>
        <div className="rounded-[8px] border border-[#dbe4d8] bg-[#f8faf6] p-4 text-center">
          <div className="text-2xl font-bold text-[#b86b16]">{stats.dueCount}</div>
          <div className="mt-1 text-xs text-slate-500">待复习</div>
        </div>
        <div className="rounded-[8px] border border-[#dbe4d8] bg-[#f8faf6] p-4 text-center">
          <div className="text-2xl font-bold text-[#0f8b8d]">{stats.masteredCount}</div>
          <div className="mt-1 text-xs text-slate-500">已掌握</div>
        </div>
      </div>
    </Panel>
  );
}

export default function LearningReportPage() {
  const [searchParams] = useSearchParams();
  const routeScope = buildLearningRouteScope(searchParams);
  const viewer = useLearningViewer(routeScope);
  const evidenceScope = buildLearningEvidenceRecordQueryScope(viewer);

  const trainingAttempts = useRecentTrainingAttempts({
    userId: viewer.userId,
    ...evidenceScope,
    limit: 100,
  });

  const { totalSessions, totalQuestions, totalCorrect, overallAccuracy } = useMemo(
    () => computeSummaryMetrics(trainingAttempts.records),
    [trainingAttempts.records]
  );

  const streak = useMemo(
    () => calculateStreak(trainingAttempts.records),
    [trainingAttempts.records]
  );

  const trendData = useMemo(
    () => computeAccuracyTrend(trainingAttempts.records),
    [trainingAttempts.records]
  );

  const sectionBreakdown = useMemo(
    () => computeSectionBreakdown(trainingAttempts.records),
    [trainingAttempts.records]
  );

  const summaryMetrics: MetricModel[] = useMemo(
    () => [
      {
        label: '总训练次数',
        value: String(totalSessions),
        tone: 'neutral' as UiTone,
      },
      {
        label: '已答题数',
        value: String(totalQuestions),
        tone: 'neutral' as UiTone,
      },
      {
        label: '总正确数',
        value: String(totalCorrect),
        tone: 'neutral' as UiTone,
      },
      {
        label: '综合准确率',
        value: `${overallAccuracy}%`,
        tone: overallAccuracy >= 80 ? 'success' : overallAccuracy >= 60 ? 'current' : 'warning',
      },
    ],
    [totalSessions, totalQuestions, totalCorrect, overallAccuracy]
  );

  const recentRecordsItems: RecentTrainingRecordItem[] = trainingAttempts.records
    .slice(0, 10)
    .map((r) => ({
      id: r.id,
      moduleLabel: r.moduleLabel,
      intensityLabel: r.intensityLabel,
      totalQuestions: r.totalQuestions,
      correctCount: r.correctCount,
      accuracy: r.accuracy,
      durationSeconds: r.durationSeconds,
    }));

  const recordsStatus =
    trainingAttempts.status === 'loading'
      ? 'loading'
      : trainingAttempts.status === 'unavailable'
        ? 'unavailable'
        : 'ready';

  return (
    <LearningShell viewer={viewer} activeNav="workspace" statusLabel="留存工具">
      <div className="space-y-6">
        <PageIntro
          eyebrow="留存工具 · 非主闭环"
          title="训练统计"
          subtitle="历史正确率与连胜是归档视图。能力结论和下一步处方请回到能力诊断。"
          secondaryAction={buildRetentionToolBackAction(routeScope)}
          primaryAction={buildRetentionReportPrimaryAction(routeScope)}
        />

        <MetricStrip metrics={summaryMetrics} />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div className="md:col-span-2 space-y-6">
            <Panel title="近 7 天准确率趋势" subtitle="每日训练准确率变化">
              {trainingAttempts.status === 'loading' ? (
                <p className="text-sm text-slate-500">加载中...</p>
              ) : (
                <AccuracyTrendChart data={trendData} />
              )}
            </Panel>

            <Panel title="分项准确率" subtitle="按 Skill 模块汇总">
              {trainingAttempts.status === 'loading' ? (
                <p className="text-sm text-slate-500">加载中...</p>
              ) : (
                <SectionBreakdownPanel breakdown={sectionBreakdown} />
              )}
            </Panel>
          </div>

          <div className="space-y-6">
            <Panel title="训练连续性" subtitle="保持每日训练的习惯">
              <StreakDisplay streak={streak} />
            </Panel>

            <MistakeStatsPanel />
          </div>
        </div>

        <RecentTrainingRecordsPanel
          records={recentRecordsItems}
          status={recordsStatus}
          title="最近训练记录"
          subtitle="服务端训练证据归档"
          emptyMessage="暂无训练记录。完成一组专项训练后会出现在这里。"
        />
      </div>
    </LearningShell>
  );
}
